import { Icon } from "@iconify/react/dist/iconify.js";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Modal } from "react-bootstrap";
import { handleError, handleSuccess } from "../CommonJquery/CommonJquery";
import MasterLayout from "../masterLayout/MasterLayout";
import {
  bulk_upload_menu,
  delete_category,
  delete_menu_items,
  get_menu_data,
  move_menu_items,
  preview_menu_upload,
  save_category,
  save_menu_item,
  server_post_data,
  update_category_status,
  update_item_status,
} from "../ServiceConnection/serviceconnection";
import Loader from "./Loader";
import { downloadCsv, toCsv, todayStamp } from "../components/csvExport";
import "../components/CSS/gmb-premium.css";
import "../components/CSS/menu-manager.css";

/* ADAPT: however your app supplies the logged-in business id. */
const BUS_ID = localStorage.getItem("bus_id") || "";

/* ADAPT: must match VEG_LABELS in menu_views.py and VEG_WORDS in menu_bulk.py.
   These three places define what veg_nonveg means and have to agree. */
const VEG_TYPES = [
  { key: 0, label: "Veg", dot: "" },
  { key: 1, label: "Non-Veg", dot: "nonveg" },
  { key: 2, label: "Egg", dot: "egg" },
];

const emptyItem = {
  menu_id: "",
  cate_id: "",
  menu_name: "",
  description: "",
  menu_price: "",
  discount_price: "",
  veg_nonveg: 0,
  ingredient_status: "N",
  menu_image: "",
};

const emptyCategory = { category_id: "", category_name: "", short_note: "" };

const rupees = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

function MenuManager() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const [activeCategory, setActiveCategory] = useState(null); // null = all
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(() => new Set());

  const [showItemModal, setShowItemModal] = useState(false);
  const [itemForm, setItemForm] = useState(emptyItem);

  const [showCatModal, setShowCatModal] = useState(false);
  const [catForm, setCatForm] = useState(emptyCategory);

  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleteCatTarget, setDeleteCatTarget] = useState(null);
  const [moveToCategory, setMoveToCategory] = useState("");

  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  const busyRef = useRef(false);
  const searchTimer = useRef(null);

  /* ------------------------- api ------------------------- */
  const call = useCallback(async (url, fields = {}, files = {}) => {
    const fd = new FormData();
    Object.entries(fields).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") fd.append(k, v);
    });
    Object.entries(files).forEach(([k, f]) => f && fd.append(k, f));

    try {
      const res = await server_post_data(url, fd);
      const parts = res.message.split("~@~");
      if (parseInt(parts[0]) === 1) {
        handleError(parts[1]);
        return null;
      }
      const parsed = JSON.parse(parts[1]);
      if (parsed.error) {
        handleError(parsed.message || "Something went wrong");
        return null;
      }
      return parsed;
    } catch (e) {
      console.log(e)
      handleError("network");
      return null;
    }
  }, []);

  const load = useCallback(
    async (opts = {}) => {
      if (busyRef.current) return;
      busyRef.current = true;
      setLoading(true);

      const d = await call(get_menu_data, {
        bus_id: BUS_ID,
        search: opts.search ?? search,
        include_hidden: "1",
      });
      if (d) setData(d);

      setLoading(false);
      busyRef.current = false;
    },
    [call, search],
  );

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Searching hits the database, so it waits for a pause in typing rather than
     firing on every keystroke. */
  const onSearchChange = (value) => {
    setSearch(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => load({ search: value }), 400);
  };

  /* ------------------------- derived ------------------------- */
  const categories = data?.categories || [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const allItems = data?.items || [];

  const items = useMemo(() => {
    if (activeCategory === null) return allItems;
    if (activeCategory === "none") return allItems.filter((i) => !i.categoryId);
    return allItems.filter((i) => i.categoryId === activeCategory);
  }, [allItems, activeCategory]);

  const uncategorisedCount = allItems.filter((i) => !i.categoryId).length;

  const toggleSelect = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const selectAllVisible = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((i) => i.id)));
  };

  const clearSelection = () => setSelected(new Set());

  /* ------------------------- items ------------------------- */
  const openNewItem = () => {
    setItemForm({
      ...emptyItem,
      cate_id:
        activeCategory && activeCategory !== "none" ? activeCategory : "",
    });
    setShowItemModal(true);
  };

  const openEditItem = (item) => {
    setItemForm({
      menu_id: item.id,
      cate_id: item.categoryId || "",
      menu_name: item.name,
      description: item.description,
      menu_price: String(item.price),
      discount_price: item.discountPrice ? String(item.discountPrice) : "",
      veg_nonveg: item.vegNonveg,
      ingredient_status: item.ingredientStatus || "N",
      menu_image: item.image,
    });
    setShowItemModal(true);
  };

  const saveItem = async () => {
    if (!itemForm.menu_name.trim()) {
      handleError("Item name is required");
      return;
    }
    setLoading(true);
    const d = await call(save_menu_item, { bus_id: BUS_ID, ...itemForm });
    setLoading(false);
    if (d) {
      handleSuccess(d.message);
      setShowItemModal(false);
      load();
    }
  };

  const toggleVisibility = async (item) => {
    // Flipped locally first so the switch responds instantly; a failed call
    // reloads and puts it back where it was.
    setData((prev) => ({
      ...prev,
      items: prev.items.map((i) =>
        i.id === item.id ? { ...i, isVisible: !i.isVisible } : i,
      ),
    }));

    const d = await call(update_item_status, {
      bus_id: BUS_ID,
      menu_ids: String(item.id),
      is_visible: item.isVisible ? "0" : "1",
    });
    if (!d) load();
  };

  const bulkVisibility = async (visible) => {
    setLoading(true);
    const d = await call(update_item_status, {
      bus_id: BUS_ID,
      menu_ids: Array.from(selected).join(","),
      is_visible: visible ? "1" : "0",
    });
    setLoading(false);
    if (d) {
      handleSuccess(d.message);
      clearSelection();
      load();
    }
  };

  const bulkMove = async (cateId) => {
    setLoading(true);
    const d = await call(move_menu_items, {
      bus_id: BUS_ID,
      menu_ids: Array.from(selected).join(","),
      cate_id: cateId,
    });
    setLoading(false);
    if (d) {
      handleSuccess(d.message);
      clearSelection();
      load();
    }
  };

  const doDeleteItems = async () => {
    setLoading(true);
    const d = await call(delete_menu_items, {
      bus_id: BUS_ID,
      menu_ids: confirmDelete.join(","),
    });
    setLoading(false);
    setConfirmDelete(null);
    if (d) {
      handleSuccess(d.message);
      clearSelection();
      load();
    }
  };

  /* ------------------------- categories ------------------------- */
  const saveCat = async () => {
    if (!catForm.category_name.trim()) {
      handleError("Category name is required");
      return;
    }
    setLoading(true);
    const d = await call(save_category, { bus_id: BUS_ID, ...catForm });
    setLoading(false);
    if (d) {
      handleSuccess(d.message);
      setShowCatModal(false);
      load();
    }
  };

  const toggleCategory = async (category) => {
    setLoading(true);
    const d = await call(update_category_status, {
      bus_id: BUS_ID,
      category_id: category.id,
      is_visible: category.isVisible ? "0" : "1",
    });
    setLoading(false);
    if (d) {
      handleSuccess(d.message);
      load();
    }
  };

  const doDeleteCategory = async () => {
    setLoading(true);
    const d = await call(delete_category, {
      bus_id: BUS_ID,
      category_id: deleteCatTarget.id,
      move_to_category_id: moveToCategory,
    });
    setLoading(false);
    setDeleteCatTarget(null);
    setMoveToCategory("");
    if (d) {
      handleSuccess(d.message);
      if (activeCategory === deleteCatTarget.id) setActiveCategory(null);
      load();
    }
  };

  /* ------------------------- upload ------------------------- */
  const onPickFile = async (file) => {
    if (!file) return;
    setUploadFile(file);
    setPreview(null);
    setUploadResult(null);

    setLoading(true);
    const d = await call(
      preview_menu_upload,
      { bus_id: BUS_ID },
      { menu_file: file },
    );
    setLoading(false);
    if (d) setPreview(d);
    else setUploadFile(null);
  };

  const doUpload = async () => {
    if (!uploadFile) return;
    setLoading(true);
    const d = await call(
      bulk_upload_menu,
      { bus_id: BUS_ID, replace_existing: replaceExisting ? "1" : "0" },
      { menu_file: uploadFile },
    );
    setLoading(false);
    if (d) {
      setUploadResult(d);
      setPreview(null);
      load();
    }
  };

  const resetUpload = () => {
    setShowUpload(false);
    setUploadFile(null);
    setPreview(null);
    setUploadResult(null);
    setReplaceExisting(false);
  };

  /* ------------------------- csv ------------------------- */

  /* column headers match what the importer accepts, so an exported file can be
     edited in Excel and uploaded straight back. That round trip is how most
     owners will do a price revision, and it only works if these strings stay
     in step with COLUMN_ALIASES in menu_bulk.py. */
  const CSV_HEADERS = [
    "Category",
    "Name",
    "Description",
    "Price",
    "Discount Price",
    "Veg NonVeg",
    "Available",
  ];

  const exportMenu = () => {
    if (!allItems.length) {
      handleError("There is nothing to export yet");
      return;
    }

    const rows = allItems.map((i) => [
      i.categoryName || "",
      i.name || "",
      i.description || "",
      i.price || "",
      i.discountPrice || "",
      i.vegLabel || "",
      i.isVisible ? "Yes" : "No",
    ]);

    downloadCsv(`menu-${todayStamp()}.csv`, toCsv(CSV_HEADERS, rows));

    // Exporting whatever is loaded means an active search silently narrows the
    // file. Saying so is cheaper than either blocking the export or quietly
    // handing someone a partial menu they think is a backup.
    handleSuccess(
      search
        ? `Exported ${rows.length} items matching "${search}"`
        : `Exported ${rows.length} items`,
    );
  };

  const downloadTemplate = () => {
    /* Filled with plausible rows rather than left blank. An owner downloading
       a headers-only template guesses wrong about the veg column and the price
       format often enough that the first upload fails. */
    const sample = [
      [
        "Starters",
        "Paneer Tikka",
        "Char-grilled cottage cheese with mint chutney",
        "280",
        "250",
        "Veg",
        "Yes",
      ],
      [
        "Starters",
        "Chicken Seekh Kebab",
        "Minced chicken skewers, tandoor cooked",
        "340",
        "",
        "Non-Veg",
        "Yes",
      ],
      [
        "Main Course",
        "Dal Makhani",
        "Slow cooked black lentils",
        "260",
        "",
        "Veg",
        "Yes",
      ],
      ["Beverages", "Masala Chai", "", "60", "", "Veg", "Yes"],
    ];

    downloadCsv("menu-template.csv", toCsv(CSV_HEADERS, sample));
  };

  const summary = data?.summary;

  return (
    <MasterLayout>
      <div className="gmb-premium menu-mgr menu-page">
        {loading && <Loader />}

        {/* ==================== TOPBAR ==================== */}
        <div className="gmb-topbar">
          <div className="gmb-topbar-title">
            <h5 className="gmb-display">Menu</h5>
            <span className="gmb-royal-badge">
              <Icon icon="mdi:silverware-fork-knife" /> Categories & Items
            </span>
          </div>
          <div className="gmb-topbar-actions">
            <button
              className="btn btn-outline-primary-600"
              onClick={exportMenu}
            >
              <Icon icon="mdi:download-outline" /> Export
            </button>
            <button
              className="btn btn-outline-primary-600"
              onClick={() => setShowUpload(true)}
            >
              <Icon icon="mdi:upload-outline" /> Bulk upload
            </button>
            <button className="btn btn-primary-600" onClick={openNewItem}>
              <Icon icon="ic:baseline-plus" /> Add item
            </button>
          </div>
        </div>

        {/* ==================== SUMMARY ==================== */}
        {summary && (
          <div className="mn-summary">
            <div className="mn-summary-lead">
              <b>{summary.totalItems}</b>
              <span>
                items across {summary.totalCategories} categories.{" "}
                {summary.totalItems - summary.visibleItems > 0
                  ? `${summary.totalItems - summary.visibleItems} currently hidden.`
                  : "All visible right now."}
              </span>
            </div>
            <div className="mn-summary-stats">
              <div>
                <b>{summary.visibleItems}</b>
                <small>Visible</small>
              </div>
              <div>
                <b>{summary.visibleCategories}</b>
                <small>Visible sections</small>
              </div>
              {summary.uncategorisedItems > 0 && (
                <div>
                  <b>{summary.uncategorisedItems}</b>
                  <small>Uncategorised</small>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ==================== TOOLBAR / BULK BAR ==================== */}
        {selected.size > 0 ? (
          <div className="mn-bulkbar">
            <span className="mn-bulkbar-count">{selected.size} selected</span>
            <button
              className="btn btn-outline-primary-600 btn-sm"
              onClick={() => bulkVisibility(true)}
            >
              <Icon icon="mdi:eye-outline" /> Show
            </button>
            <button
              className="btn btn-outline-primary-600 btn-sm"
              onClick={() => bulkVisibility(false)}
            >
              <Icon icon="mdi:eye-off-outline" /> Hide
            </button>
            <select
              className="form-control form-control-sm"
              style={{ maxWidth: 190 }}
              value=""
              onChange={(e) => e.target.value && bulkMove(e.target.value)}
            >
              <option value="">Move to category…</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <button
              className="btn btn-outline-danger-600 btn-sm"
              onClick={() => setConfirmDelete(Array.from(selected))}
            >
              <Icon icon="fluent:delete-24-regular" /> Delete
            </button>
            <button
              className="btn btn-outline-primary-600 btn-sm"
              onClick={clearSelection}
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="mn-toolbar">
            <div className="mn-search">
              <Icon icon="mdi:magnify" />
              <input
                className="form-control"
                placeholder="Search items…"
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
              />
            </div>
            <button
              className="btn btn-outline-primary-600"
              onClick={() => {
                setCatForm(emptyCategory);
                setShowCatModal(true);
              }}
            >
              <Icon icon="mdi:folder-plus-outline" /> Add category
            </button>
          </div>
        )}

        {/* ==================== TWO PANES ==================== */}
        <div className="mn-layout">
          {/* ---------- categories ---------- */}
          <div className="mn-pane">
            <div className="mn-pane-head">
              <b>Categories</b>
              <button
                className="mn-pane-add"
                title="Add a category"
                onClick={() => {
                  setCatForm(emptyCategory);
                  setShowCatModal(true);
                }}
              >
                <Icon icon="mdi:plus" />
              </button>
            </div>

            <div className="mn-pane-body">
              <div
                className={`mn-cat ${activeCategory === null ? "active" : ""}`}
                onClick={() => setActiveCategory(null)}
              >
                <div className="mn-cat-body">
                  <div className="mn-cat-name">All items</div>
                  <div className="mn-cat-count">{allItems.length} items</div>
                </div>
              </div>

              {categories.map((c) => (
                <div
                  className={`mn-cat ${activeCategory === c.id ? "active" : ""} ${
                    c.isVisible ? "" : "hidden-cat"
                  }`}
                  key={c.id}
                  onClick={() => setActiveCategory(c.id)}
                >
                  <div className="mn-cat-body">
                    <div className="mn-cat-name">
                      {c.name}
                      {!c.isVisible && (
                        <span className="mn-hidden-chip">hidden</span>
                      )}
                    </div>
                    <div className="mn-cat-count">
                      {c.itemCount} item{c.itemCount === 1 ? "" : "s"}
                      {c.itemCount !== c.visibleItemCount &&
                        ` · ${c.itemCount - c.visibleItemCount} hidden`}
                    </div>
                  </div>

                  <div className="mn-cat-actions">
                    <button
                      className="mn-icon-btn"
                      title={
                        c.isVisible
                          ? "Hide from customers"
                          : "Show to customers"
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleCategory(c);
                      }}
                    >
                      <Icon
                        icon={
                          c.isVisible
                            ? "mdi:eye-outline"
                            : "mdi:eye-off-outline"
                        }
                      />
                    </button>
                    <button
                      className="mn-icon-btn"
                      title="Edit"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCatForm({
                          category_id: c.id,
                          category_name: c.name,
                          short_note: c.shortNote,
                        });
                        setShowCatModal(true);
                      }}
                    >
                      <Icon icon="lucide:edit" />
                    </button>
                    <button
                      className="mn-icon-btn danger"
                      title="Delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteCatTarget(c);
                        setMoveToCategory("");
                      }}
                    >
                      <Icon icon="fluent:delete-24-regular" />
                    </button>
                  </div>
                </div>
              ))}

              {uncategorisedCount > 0 && (
                <div
                  className={`mn-cat ${activeCategory === "none" ? "active" : ""}`}
                  onClick={() => setActiveCategory("none")}
                >
                  <div className="mn-cat-body">
                    <div
                      className="mn-cat-name"
                      style={{ color: "var(--gmb-ink-3)" }}
                    >
                      Uncategorised
                    </div>
                    <div className="mn-cat-count">
                      {uncategorisedCount} items
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ---------- items ---------- */}
          <div className="mn-pane">
            <div className="mn-pane-head">
              <input
                type="checkbox"
                checked={items.length > 0 && selected.size === items.length}
                onChange={selectAllVisible}
                style={{
                  width: 16,
                  height: 16,
                  accentColor: "var(--primary-600)",
                }}
                title="Select all"
              />
              <b>
                {activeCategory === null
                  ? "All items"
                  : activeCategory === "none"
                    ? "Uncategorised"
                    : categories.find((c) => c.id === activeCategory)?.name ||
                      "Items"}
              </b>
              <span style={{ fontSize: 11, color: "var(--gmb-ink-3)" }}>
                {items.length}
              </span>
              <button
                className="mn-pane-add"
                title="Add an item"
                onClick={openNewItem}
              >
                <Icon icon="mdi:plus" />
              </button>
            </div>

            <div className="mn-pane-body">
              {items.length === 0 ? (
                <div className="mn-empty">
                  <div className="mn-empty-icon">
                    <Icon icon="mdi:silverware-variant" />
                  </div>
                  <b>{search ? "Nothing matched" : "No items yet"}</b>
                  <p>
                    {search
                      ? "Try a different word, or clear the search."
                      : "Add items one at a time, or upload your existing menu as a spreadsheet."}
                  </p>
                  {!search && (
                    <div className="d-flex gap-2 justify-content-center">
                      <button
                        className="btn btn-primary-600"
                        onClick={openNewItem}
                      >
                        <Icon icon="ic:baseline-plus" /> Add item
                      </button>
                      <button
                        className="btn btn-outline-primary-600"
                        onClick={() => setShowUpload(true)}
                      >
                        <Icon icon="mdi:upload-outline" /> Bulk upload
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                items.map((i) => (
                  <div
                    className={`mn-item ${selected.has(i.id) ? "selected" : ""} ${
                      i.isVisible ? "" : "unavailable"
                    }`}
                    key={i.id}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(i.id)}
                      onChange={() => toggleSelect(i.id)}
                    />

                    {i.image ? (
                      <img className="mn-item-thumb" src={i.image} alt="" />
                    ) : (
                      <span className="mn-item-thumb-empty">
                        <Icon icon="mdi:food-outline" />
                      </span>
                    )}

                    <div className="mn-item-body">
                      <div className="mn-item-name">
                        <span
                            className={`mn-dot ${
                              VEG_TYPES.find((v) => v.key === i.vegNonveg)
                                ?.dot || ""
                            }`}
                          />
                        {i.name}
                      </div>

                      {i.description && (
                        <div className="mn-item-desc">{i.description}</div>
                      )}

                      {i.categoryName && (
                        <div className="mn-item-meta">
                          <span className="mn-tag">{i.categoryName}</span>
                        </div>
                      )}
                    </div>

                    <div className="mn-item-price">
                      <b>
                        {i.hasOffer && <s>{rupees(i.price)}</s>}
                        {rupees(i.effectivePrice)}
                      </b>
                    </div>

                    <button
                      className={`mn-switch ${i.isVisible ? "on" : ""}`}
                      onClick={() => toggleVisibility(i)}
                      title={i.isVisible ? "Visible to customers" : "Hidden"}
                    />

                    <div className="mn-item-actions">
                      <button
                        className="mn-icon-btn"
                        title="Edit"
                        onClick={() => openEditItem(i)}
                      >
                        <Icon icon="lucide:edit" />
                      </button>
                      <button
                        className="mn-icon-btn danger"
                        title="Delete"
                        onClick={() => setConfirmDelete([i.id])}
                      >
                        <Icon icon="fluent:delete-24-regular" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* ==================== ITEM MODAL ==================== */}
        <Modal
          className="gmb-modal"
          size="lg"
          centered
          show={showItemModal}
          onHide={() => setShowItemModal(false)}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {itemForm.menu_id ? "Edit item" : "Add item"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mn-form-row">
              <div style={{ flex: 2 }}>
                <label className="gmb-field-label">Item name *</label>
                <input
                  className="form-control mb-3"
                  placeholder="Eg: Paneer Tikka"
                  value={itemForm.menu_name}
                  onChange={(e) =>
                    setItemForm((s) => ({ ...s, menu_name: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className="gmb-field-label">Category</label>
                <select
                  className="form-control mb-3"
                  value={itemForm.cate_id}
                  onChange={(e) =>
                    setItemForm((s) => ({ ...s, cate_id: e.target.value }))
                  }
                >
                  <option value="">No category</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <label className="gmb-field-label">Description</label>
            <textarea
              className="form-control mb-3"
              rows={2}
              maxLength={300}
              placeholder="What is in it, how it is served"
              value={itemForm.description}
              onChange={(e) =>
                setItemForm((s) => ({ ...s, description: e.target.value }))
              }
            />

            <div className="mn-form-row">
              <div>
                <label className="gmb-field-label">Price *</label>
                <input
                  className="form-control mb-3"
                  placeholder="280"
                  inputMode="decimal"
                  value={itemForm.menu_price}
                  onChange={(e) =>
                    setItemForm((s) => ({ ...s, menu_price: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className="gmb-field-label">Discount price</label>
                <input
                  className="form-control mb-3"
                  placeholder="Leave blank if none"
                  inputMode="decimal"
                  value={itemForm.discount_price}
                  onChange={(e) =>
                    setItemForm((s) => ({
                      ...s,
                      discount_price: e.target.value,
                    }))
                  }
                />
              </div>
            </div>

            <label className="gmb-field-label">Food type</label>
            <div className="mn-type-pills mb-3">
              {VEG_TYPES.map((v) => (
                <button
                  key={v.key}
                  type="button"
                  className={`mn-type-pill ${
                    itemForm.veg_nonveg === v.key ? "active" : ""
                  }`}
                  onClick={() =>
                    setItemForm((s) => ({ ...s, veg_nonveg: v.key }))
                  }
                >
                  {v.dot !== null && <span className={`mn-dot ${v.dot}`} />}
                  {v.label}
                </button>
              ))}
            </div>

            <label
              className="d-flex align-items-center gap-2"
              style={{ fontSize: 13 }}
            >
              <input
                type="checkbox"
                checked={itemForm.ingredient_status === "Y"}
                onChange={(e) =>
                  setItemForm((s) => ({
                    ...s,
                    ingredient_status: e.target.checked ? "Y" : "N",
                  }))
                }
                style={{ accentColor: "var(--primary-600)" }}
              />
              Has ingredient options
            </label>
          </Modal.Body>
          <Modal.Footer>
            <button
              className="btn btn-outline-primary-600"
              onClick={() => setShowItemModal(false)}
            >
              Cancel
            </button>
            <button className="btn btn-primary-600" onClick={saveItem}>
              {itemForm.menu_id ? "Save changes" : "Add item"}
            </button>
          </Modal.Footer>
        </Modal>

        {/* ==================== CATEGORY MODAL ==================== */}
        <Modal
          className="gmb-modal"
          centered
          show={showCatModal}
          onHide={() => setShowCatModal(false)}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {catForm.category_id ? "Edit category" : "Add category"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <label className="gmb-field-label">Category name *</label>
            <input
              className="form-control mb-3"
              placeholder="Eg: Starters"
              maxLength={191}
              value={catForm.category_name}
              onChange={(e) =>
                setCatForm((s) => ({ ...s, category_name: e.target.value }))
              }
            />

            <label className="gmb-field-label">Short note</label>
            <input
              className="form-control"
              placeholder="Optional, shown under the heading"
              maxLength={191}
              value={catForm.short_note}
              onChange={(e) =>
                setCatForm((s) => ({ ...s, short_note: e.target.value }))
              }
            />
          </Modal.Body>
          <Modal.Footer>
            <button
              className="btn btn-outline-primary-600"
              onClick={() => setShowCatModal(false)}
            >
              Cancel
            </button>
            <button className="btn btn-primary-600" onClick={saveCat}>
              {catForm.category_id ? "Save changes" : "Add category"}
            </button>
          </Modal.Footer>
        </Modal>

        {/* ==================== DELETE CATEGORY ==================== */}
        <Modal
          className="gmb-modal"
          centered
          show={!!deleteCatTarget}
          onHide={() => setDeleteCatTarget(null)}
        >
          <Modal.Header closeButton>
            <Modal.Title>Delete category</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {deleteCatTarget && (
              <>
                <p style={{ fontSize: 14, lineHeight: 1.65 }}>
                  Remove <b>{deleteCatTarget.name}</b>?
                  {deleteCatTarget.itemCount > 0 && (
                    <>
                      {" "}
                      It has {deleteCatTarget.itemCount} item
                      {deleteCatTarget.itemCount === 1 ? "" : "s"} — the items
                      are kept, not deleted.
                    </>
                  )}
                </p>

                {deleteCatTarget.itemCount > 0 && (
                  <>
                    <label className="gmb-field-label">
                      Move those items to
                    </label>
                    <select
                      className="form-control"
                      value={moveToCategory}
                      onChange={(e) => setMoveToCategory(e.target.value)}
                    >
                      <option value="">Leave uncategorised</option>
                      {categories
                        .filter((c) => c.id !== deleteCatTarget.id)
                        .map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                    </select>
                  </>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button
              className="btn btn-outline-primary-600"
              onClick={() => setDeleteCatTarget(null)}
            >
              Cancel
            </button>
            <button className="btn btn-danger-600" onClick={doDeleteCategory}>
              Delete category
            </button>
          </Modal.Footer>
        </Modal>

        {/* ==================== DELETE ITEMS ==================== */}
        <Modal
          className="gmb-modal"
          size="sm"
          centered
          show={!!confirmDelete}
          onHide={() => setConfirmDelete(null)}
        >
          <Modal.Body className="text-center">
            <Icon
              icon="fluent:delete-24-regular"
              style={{ fontSize: 34, color: "var(--danger-main)" }}
            />
            <p className="mt-2 mb-0" style={{ fontSize: 14 }}>
              Delete {confirmDelete?.length}{" "}
              {confirmDelete?.length === 1 ? "item" : "items"}?
            </p>
            <p
              style={{
                fontSize: 11.5,
                color: "var(--gmb-ink-3)",
                marginTop: 8,
              }}
            >
              Past orders that include them are not affected.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <button
              className="btn btn-outline-primary-600"
              onClick={() => setConfirmDelete(null)}
            >
              Cancel
            </button>
            <button className="btn btn-danger-600" onClick={doDeleteItems}>
              Delete
            </button>
          </Modal.Footer>
        </Modal>

        {/* ==================== BULK UPLOAD ==================== */}
        <Modal
          className="gmb-modal"
          size="lg"
          centered
          show={showUpload}
          onHide={resetUpload}
        >
          <Modal.Header closeButton>
            <Modal.Title>Bulk upload menu</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {uploadResult ? (
              <>
                <div className="mn-result-row">
                  <span className="mn-result-num ok">
                    {uploadResult.created}
                  </span>
                  <span>items added</span>
                </div>
                <div className="mn-result-row">
                  <span className="mn-result-num warn">
                    {uploadResult.updated}
                  </span>
                  <span>existing items updated</span>
                </div>
                {uploadResult.failed > 0 && (
                  <div className="mn-result-row">
                    <span className="mn-result-num bad">
                      {uploadResult.failed}
                    </span>
                    <span>rows skipped</span>
                  </div>
                )}

                {uploadResult.categoriesCreated?.length > 0 && (
                  <p
                    style={{
                      fontSize: 12.5,
                      color: "var(--gmb-ink-2)",
                      marginTop: 12,
                    }}
                  >
                    New categories created:{" "}
                    <b>{uploadResult.categoriesCreated.join(", ")}</b>
                  </p>
                )}

                {uploadResult.errors?.length > 0 && (
                  <div className="mn-errors">
                    {uploadResult.errors.map((e, i) => (
                      <div key={i}>
                        <b>Row {e.row}:</b> {e.reason}
                      </div>
                    ))}
                    {uploadResult.hasMoreErrors && (
                      <div style={{ opacity: 0.7 }}>
                        …and more. Fix these first, then upload again.
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : preview ? (
              <>
                <div className="mn-file-chip mb-3">
                  <Icon
                    icon="mdi:file-document-outline"
                    style={{ fontSize: 22, color: "var(--gmb-ink-3)" }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <b>{uploadFile?.name}</b>
                    <small>{preview.totalRows} rows found</small>
                  </div>
                  <button
                    className="mn-icon-btn"
                    onClick={() => {
                      setUploadFile(null);
                      setPreview(null);
                    }}
                  >
                    <Icon icon="mdi:close" />
                  </button>
                </div>

                <label className="gmb-field-label">Columns we recognised</label>
                <div className="mn-mapped">
                  {preview.mappedColumns.map((c) => (
                    <span key={c}>{c.replace(/_/g, " ")}</span>
                  ))}
                  {preview.unknownColumns?.map((c) => (
                    <span className="unknown" key={c}>
                      {c} (ignored)
                    </span>
                  ))}
                </div>

                <label className="gmb-field-label">First few rows</label>
                <div style={{ overflowX: "auto" }}>
                  <table className="mn-preview-table">
                    <thead>
                      <tr>
                        {preview.mappedColumns.map((c) => (
                          <th key={c}>{c.replace(/_/g, " ")}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.sample.map((row, i) => (
                        <tr key={i}>
                          {preview.mappedColumns.map((c) => (
                            <td key={c}>{row[c]}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p
                  style={{
                    fontSize: 12,
                    color: "var(--gmb-ink-3)",
                    marginTop: 12,
                  }}
                >
                  {preview.note}
                </p>

                <label className="mn-replace-warn">
                  <input
                    type="checkbox"
                    checked={replaceExisting}
                    onChange={(e) => setReplaceExisting(e.target.checked)}
                  />
                  <span>
                    <b>Replace the whole menu.</b> Every current item is removed
                    first and only this file remains. Leave this unticked to add
                    and update instead.
                  </span>
                </label>
              </>
            ) : (
              <>
                <label className="mn-drop">
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xlsm"
                    onChange={(e) => onPickFile(e.target.files[0])}
                  />
                  <div className="mn-drop-icon">
                    <Icon icon="mdi:cloud-upload-outline" />
                  </div>
                  <b>Choose a CSV or Excel file</b>
                  <span>Up to 5 MB, 2000 rows at a time</span>
                </label>

                <div className="d-flex align-items-center gap-2 mt-3 flex-wrap">
                  <button
                    className="btn btn-outline-primary-600 btn-sm"
                    onClick={downloadTemplate}
                  >
                    <Icon icon="mdi:download-outline" /> Download template
                  </button>
                  <button
                    className="btn btn-outline-primary-600 btn-sm"
                    onClick={exportMenu}
                  >
                    <Icon icon="mdi:file-export-outline" /> Export current menu
                  </button>
                </div>

                <p
                  style={{
                    fontSize: 12,
                    color: "var(--gmb-ink-3)",
                    marginTop: 14,
                    lineHeight: 1.6,
                  }}
                >
                  The exported file uses the same columns as the template, so
                  you can export your menu, edit the prices in Excel, and upload
                  it straight back.
                </p>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            {uploadResult ? (
              <button className="btn btn-primary-600" onClick={resetUpload}>
                Done
              </button>
            ) : (
              <>
                <button
                  className="btn btn-outline-primary-600"
                  onClick={resetUpload}
                >
                  Cancel
                </button>
                {preview && (
                  <button
                    className={
                      replaceExisting
                        ? "btn btn-danger-600"
                        : "btn btn-primary-600"
                    }
                    onClick={doUpload}
                  >
                    {replaceExisting
                      ? `Replace menu with ${preview.totalRows} items`
                      : `Import ${preview.totalRows} rows`}
                  </button>
                )}
              </>
            )}
          </Modal.Footer>
        </Modal>
      </div>
    </MasterLayout>
  );
}

export default MenuManager;
